import React from 'react'
import { Theme, IconButton, Typography, Box } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { useTranslation } from 'react-i18next'

import FirstPageRounded from '@mui/icons-material/FirstPageRounded'
import LastPageRounded from '@mui/icons-material/LastPageRounded'
import NavigateBeforeRounded from '@mui/icons-material/NavigateBeforeRounded'
import NavigateNextRounded from '@mui/icons-material/NavigateNextRounded'

type IProps = {
  totalItems: number
  itemsPerPage: number
  page: number
  setPage: (page: number) => void
};

const useStyles = makeStyles((theme: Theme) => ({
  pagination: {
    margin: '0',
    display: 'flex',
    padding: 0,
    flexWrap: 'wrap',
    listStyle: 'none',
    alignItems: 'center',
  },
  pageLink: {
    margin: '0 1px',
  },
  disabled: {
    opacity: 0.38,
    cursor: 'default',
  },
  paginationInfo: {
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    marginRight: 'auto',
  }
}))

export default function Pagination (props: IProps) {
  const { totalItems, itemsPerPage, page, setPage } = props
  const totalPages = Math.ceil(totalItems / itemsPerPage)

  const classes = useStyles()
  const { t } = useTranslation()

  const onFirstPage = (): void => {
    setPage(1)
  }

  const onLastPage = (): void => {
    setPage(totalPages)
  }

  const onNextPage = (): void => {
    if (page < totalPages) {
      setPage(page + 1)
    }
  }

  const onPreviousPage = (): void => {
    if (page > 1) {
      setPage(page - 1)
    }
  }

  // Calculate the range of items being displayed
  const startItem = Math.min((page - 1) * itemsPerPage + 1, totalItems)
  const endItem = Math.min(page * itemsPerPage, totalItems)

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Box className={classes.paginationInfo}>
        <Typography variant="body2">
          <b>{startItem}-{endItem} von {totalItems}</b>
        </Typography>
      </Box>
      <nav>
        <ul className={classes.pagination}>
          <li>
            <IconButton
              className={`${classes.pageLink} ${page === 1 ? classes.disabled : ''}`}
              color='primary'
              component='div'
              onClick={() => onFirstPage()}
              size='large'
            >
              <FirstPageRounded />
            </IconButton>
          </li>
          <li>
            <IconButton
              className={`${classes.pageLink} ${page === 1 ? classes.disabled : ''}`}
              color='primary'
              component='div'
              onClick={() => onPreviousPage()}
              size='large'
            >
              <NavigateBeforeRounded />
            </IconButton>
          </li>
          <li>
            <IconButton
              className={`${classes.pageLink} ${page === totalPages ? classes.disabled : ''}`}
              color='primary'
              component='div'
              onClick={() => onNextPage()}
              size='large'
            >
              <NavigateNextRounded />
            </IconButton>
          </li>
          <li>
            <IconButton
              className={`${classes.pageLink} ${page === totalPages ? classes.disabled : ''}`}
              color='primary'
              component='div'
              onClick={() => onLastPage()}
              size='large'
            >
              <LastPageRounded />
            </IconButton>
          </li>
        </ul>
      </nav>
    </Box>
  )
}
